/**
 * DECISIONS 416 (lane E: "make the bots' intent legible on screen, from the state they already hold"): the bots' callouts —
 * a short word over a soldier or a squad's leader when it starts something ("Moving!", "Covering!", "Frag out!"). Read from
 * the state only (the brains in `state.actionBrains`, the leaders' steps, the plays, the soldiers' own reload, throw and
 * bandage): the Match is never touched. Presentation; the Lab's Callouts row turns it on.
 *
 * A callout is one row of CALLOUTS: the fact it is called on (`when`, a pure read of the state), the words, who calls it (any
 * member, or the squad's leader), where it shows (at the shoulder, in the command view, both) and its rank. A call is made
 * when its fact turns true for a unit and not again until the fact has gone false — so a reload is called once a reload, a
 * bound once a bound. A new skill's callout is one more row.
 *
 * The watch (`CalloutWatch`) looks a few times a second, keeps each unit's facts from its last look, and lets through at most
 * one call a unit at a time (the higher rank wins a tie), none from a unit within `CALLOUT_LOOK.rest` s of its last.
 */
import type { ActionBrainState, GameState, LeadStep, MatchSquad, PlayerState, Play, SkillTag, Vec3 } from '../types';
import { headingOf, projectToTangent, rightOf } from '../core/surfaceWalk';
import { vecDot, vecNormalize, vecSub } from '../math';
import { PHYS_DT } from '../constants/physics';
import { squadSkill } from '../core/squads';

/** 69.101: the Lab's Callouts row — off; ours (the viewer's side in play, both sides in a watched match); both (both sides always). */
export type CalloutsMode = 'off' | 'ours' | 'both';
let calloutsOverride: CalloutsMode | null = null;
/** The Lab sets the row at launch (null: the page's `?callouts=ours|both`, else off). */
export function setCalloutsMode(m: CalloutsMode | null): void { calloutsOverride = m; }
export function calloutsMode(): CalloutsMode {
  if (calloutsOverride !== null) return calloutsOverride;
  try { const q = typeof location === 'undefined' ? null : new URLSearchParams(location.search).get('callouts'); return q === 'ours' || q === 'both' ? q : 'off'; } catch { return 'off'; }
}
/** The sides whose calls show: the viewer's (`team`; null watching) in play, both in a watched match or with `both`. */
export function calloutTeams(mode: CalloutsMode, team: number | null, teams: readonly number[]): number[] {
  return mode === 'off' ? [] : mode === 'both' || team === null ? [...teams] : [team];
}

type CalloutState = Pick<GameState, 'players' | 'squads'> & Partial<Pick<GameState, 'plays' | 'actionBrains' | 'squadApart'>>;

/** What a rule reads: the unit, its brain, its squad and that squad's leader's step, the play its squad is in and its part. */
export interface CalloutCtx {
  state: CalloutState;
  tick: number;
  unit: PlayerState;
  brain: ActionBrainState | undefined;
  squad: MatchSquad | undefined;
  /** it is its squad's leader (the squad's first living member when the squad has none named) */
  leader: boolean;
  /** its squad's leader's step (squadLead.ts), on the leader's brain */
  step: LeadStep | undefined;
  play: Play | undefined;
  part: 'support' | 'assault' | undefined;
}

/** Where a call shows: over the soldier at the shoulder, over the squad in the command view, or both. */
export type CalloutWhere = 'shoulder' | 'command' | 'both';

export interface CalloutRule {
  id: string;
  /** the words (a function where they carry a side: "Contact left!") */
  say: string | ((c: CalloutCtx) => string);
  /** the fact: it is called when this turns true */
  when: (c: CalloutCtx) => boolean;
  by: 'unit' | 'leader';
  at: CalloutWhere;
  /** above a lower rank on the same look (a frag over a reload) */
  rank: number;
  /** what kind of call it is (the look's colour): the squad's drill, danger, the soldier's own state */
  tone: 'drill' | 'danger' | 'self';
  /** the fact held true this long (s) after it last read true — a fact that flickers (a sighting) is called once a spell */
  hold?: number;
  /** once a squad: no member repeats it within this long (s) of a member's call (the first to see the enemy calls the contact) */
  squad?: number;
}

/**
 * A skill's phase (ARCHITECTURE's survey, 415: `skill?: { name, phase, since }` on the commander's task and on the leader's
 * step — lane H's ambush and rally on the task, its ambush walk and lane C's skills on the step): the squad's, the leader's
 * step first. The squad card reads the same (core/squads.ts squadSkill).
 */
export function skillOf(c: Pick<CalloutCtx, 'state' | 'tick' | 'unit' | 'step'>): SkillTag | undefined {
  return squadSkill(c.state as GameState, c.unit.id, c.tick, c.step);
}

const secs = (c: CalloutCtx, t: number | undefined, within: number) => t !== undefined && c.tick - t >= 0 && (c.tick - t) * PHYS_DT < within;
const inContact = (c: CalloutCtx) => secs(c, c.brain?.contactAt, 4) || c.brain?.mode === 'fight';

/** Front, left, right or behind the unit: the side `at` (a surface direction) lies on. */
export function sideOf(unit: Pick<PlayerState, 'heading' | 'surfaceDir'>, at: Vec3): 'front' | 'left' | 'right' | 'behind' {
  const n = vecNormalize(unit.surfaceDir), h = headingOf(unit), d = projectToTangent(vecSub(vecNormalize(at), n), n);
  const a = Math.atan2(vecDot(d, rightOf(h, n)), vecDot(d, h)) * 180 / Math.PI;
  return Math.abs(a) <= 50 ? 'front' : Math.abs(a) >= 135 ? 'behind' : a > 0 ? 'right' : 'left';
}

/**
 * The callouts, one a row. A new skill's: `{ id, say, when: c => <its fact>, by, at, rank, tone }` — the fact read from
 * whatever the skill keeps in the state (its brain field, its play).
 */
export const CALLOUTS: CalloutRule[] = [
  // the squad's drill (the leader's step, 68.9; the bound's movers and coverers; the contact drill's call; the ambush)
  { id: 'moving', say: 'Moving!', when: c => c.step?.kind === 'bound' && c.step.moving.includes(c.unit.id), by: 'unit', at: 'both', rank: 3, tone: 'drill' },
  { id: 'covering', say: 'Covering!', when: c => c.step?.kind === 'bound' && !c.step.moving.includes(c.unit.id) && c.step.posts.some(([id]) => id === c.unit.id), by: 'unit', at: 'shoulder', rank: 3, tone: 'drill' },
  { id: 'hold', say: 'Hold here!', when: c => c.step?.kind === 'hold' && !c.step.ambush, by: 'leader', at: 'both', rank: 2, tone: 'drill' },
  { id: 'contactDrill', say: 'Contact — down!', when: c => c.step?.kind === 'drill' && !c.step.drill?.call, by: 'leader', at: 'both', rank: 4, tone: 'drill' },
  { id: 'pushOn', say: 'Push on!', when: c => c.step?.drill?.call === 'bound', by: 'leader', at: 'both', rank: 4, tone: 'drill' },
  { id: 'fallBack', say: 'Fall back!', when: c => c.step?.drill?.call === 'withdraw', by: 'leader', at: 'both', rank: 5, tone: 'drill' },
  { id: 'ambushMoving', say: 'Moving to ambush', when: c => { const k = skillOf(c); return k?.name === 'ambush' && k.phase.startsWith('moving'); }, by: 'leader', at: 'both', rank: 3, tone: 'drill' },
  { id: 'ambushSet', say: 'Hold fire…', when: c => !!c.step?.ambush && c.step.ambush.sprung === undefined, by: 'leader', at: 'both', rank: 3, tone: 'drill' },
  { id: 'ambushSprung', say: 'Open fire!', when: c => c.step?.ambush?.sprung !== undefined, by: 'leader', at: 'both', rank: 6, tone: 'drill' },
  // (the owner, Nov 2, on the sheet's row 3: "New callouts look good" — a skill's phase, its own words: lane H's walk out to the
  // lie on the leader's step, and the rally's two, with commanderRally on)
  { id: 'ambushWalk', say: 'Into position — quiet', when: c => { const k = skillOf(c); return k?.name === 'ambush' && k.phase === 'walking out'; }, by: 'leader', at: 'both', rank: 3, tone: 'drill' },
  { id: 'rallyBack', say: 'Fall back — rally on me!', when: c => { const k = skillOf(c); return k?.name === 'rally' && k.phase === 'falling back'; }, by: 'leader', at: 'both', rank: 5, tone: 'drill' },
  { id: 'rallyHere', say: 'Rally here!', when: c => { const k = skillOf(c); return k?.name === 'rally' && k.phase === 'rallying'; }, by: 'leader', at: 'both', rank: 3, tone: 'drill' },
  // a play (68.22): the support's fire, the assault's go, the lift; a flank's route (66.16)
  { id: 'suppress', say: 'Suppressing!', when: c => c.part === 'support' && c.play?.phase === 'assault' && !c.play.lifted, by: 'leader', at: 'both', rank: 3, tone: 'drill' },
  { id: 'goingIn', say: 'Going in!', when: c => c.part === 'assault' && c.play?.phase === 'assault' && (!c.play.via?.[c.squad?.id ?? -1] || c.play.via[c.squad!.id].state === 'go'), by: 'leader', at: 'both', rank: 4, tone: 'drill' },
  { id: 'flanking', say: c => `Flanking ${c.play && c.squad && c.play.via?.[c.squad.id] ? sideOf(c.unit, c.play.via[c.squad.id].at) === 'right' ? 'right' : 'left' : ''}`.trim() + '!', when: c => !!c.squad && c.play?.via?.[c.squad.id]?.state === 'route', by: 'leader', at: 'both', rank: 4, tone: 'drill' },
  { id: 'shiftFire', say: 'Shift fire!', when: c => c.part === 'support' && !!c.play?.lifted, by: 'leader', at: 'both', rank: 4, tone: 'drill' },
  // danger (the soldier's own brain)
  { id: 'contact', say: c => { const s = c.brain?.seenAt?.[0]; return s ? `Contact ${sideOf(c.unit, s.surfaceDir)}!` : 'Contact!'; }, when: c => (c.brain?.seenIds?.length ?? 0) > 0, by: 'unit', at: 'shoulder', rank: 4, tone: 'danger', hold: 12, squad: 12 },
  { id: 'cover', say: 'Taking cover!', when: c => !!c.brain?.fireCover && secs(c, c.brain.fireCover.lastFire, 1.5), by: 'unit', at: 'shoulder', rank: 4, tone: 'danger', hold: 6 },
  { id: 'vehicle', say: 'Vehicle — move!', when: c => c.brain?.hullNotice !== undefined, by: 'unit', at: 'shoulder', rank: 5, tone: 'danger' },
  { id: 'rocket', say: 'Rocket on the armour!', when: c => c.brain?.hullSeen !== undefined && c.unit.weapon === c.unit.secondary, by: 'unit', at: 'shoulder', rank: 5, tone: 'danger' },
  { id: 'frag', say: 'Frag out!', when: c => c.unit.throwWind?.kind === 'he', by: 'unit', at: 'shoulder', rank: 6, tone: 'danger' },
  { id: 'smoke', say: 'Smoke out!', when: c => c.unit.throwWind?.kind === 'smoke', by: 'unit', at: 'both', rank: 6, tone: 'danger' },
  { id: 'retreat', say: 'Falling back!', when: c => c.brain?.plan?.job === 'retreat', by: 'unit', at: 'shoulder', rank: 4, tone: 'danger' },
  // the soldier's own state
  { id: 'reload', say: 'Reloading!', when: c => (c.unit.reloadLeft ?? 0) > 0 && inContact(c), by: 'unit', at: 'shoulder', rank: 2, tone: 'self' },
  { id: 'hit', say: "I'm hit!", when: c => secs(c, c.brain?.hurtAt, 0.6) && c.unit.health < 50, by: 'unit', at: 'shoulder', rank: 3, tone: 'self' },
  { id: 'bandage', say: 'Patching up', when: c => (c.unit.healLeft ?? 0) > 0 && c.unit.healBy === undefined, by: 'unit', at: 'shoulder', rank: 2, tone: 'self' },
  { id: 'medic', say: 'Got you — hold still', when: c => c.unit.treating !== undefined, by: 'unit', at: 'shoulder', rank: 2, tone: 'self' },
  { id: 'rearm', say: 'Need ammo', when: c => c.brain?.rearmFor !== undefined && c.unit.mount?.bodyId === c.brain.rearmFor.hull, by: 'unit', at: 'both', rank: 2, tone: 'self' },
];

/**
 * The watch's timing: looks a second, a unit's quiet after a call (s), how long a call shows (s, then it fades over `fade`);
 * and how much shows — at the shoulder the side's calls within `shoulderReach` people and in view, `shoulderMost` at once
 * (the highest rank, then the nearest; the rest a pip), in the command view one a squad.
 */
export const CALLOUT_LOOK = { perSecond: 8, rest: 2.5, show: 2.2, fade: 0.5, shoulderReach: 40, shoulderMost: 4 };

/** A call made: who, which rule, the words, its tone and where it shows, and the tick it was made. */
export interface Callout { unitId: number; squadId: number | null; team: number; id: string; say: string; tone: CalloutRule['tone']; at: CalloutWhere; tick: number }

/** The calls' timer and memory: `look` a few times a second; `live` the calls still showing. */
export class CalloutWatch {
  private facts = new Map<number, Set<string>>();
  private lastCall = new Map<number, number>();
  /** each unit's rules' last true read (`hold`), and each squad's rules' last call (`squad`), keyed `id:rule` */
  private lastTrue = new Map<string, number>();
  private squadCall = new Map<string, number>();
  readonly live: Callout[] = [];
  constructor(private rules: readonly CalloutRule[] = CALLOUTS) {}

  /** The calls made at `tick` for the units of `teams` (a first look calls nothing: it learns the facts). */
  look(state: CalloutState, tick: number, teams: readonly number[]): Callout[] {
    const made: Callout[] = [];
    const brains = state.actionBrains ?? {};
    const squadOf = new Map<number, MatchSquad>();
    for (const sq of state.squads ?? []) for (const id of sq.unitIds) squadOf.set(id, sq);
    for (const unit of state.players) {
      if (!unit.alive || !teams.includes(unit.teamIndex) || !unit.isBot) { this.facts.delete(unit.id); continue; }   // (a respawn learns its facts afresh)
      const squad = squadOf.get(unit.id), brain = brains[unit.id];
      const leaderId = squad ? squad.leader ?? squad.unitIds.find(id => state.players.find(p => p.id === id)?.alive) : undefined;
      const step = leaderId !== undefined ? brains[leaderId]?.lead : undefined;
      const play = squad ? (state.plays ?? []).find(p => p.phase !== 'done' && (p.support.includes(squad.id) || p.assault.includes(squad.id))) : undefined;
      const c: CalloutCtx = { state, tick, unit, brain, squad, leader: leaderId === unit.id || !squad, step, play, part: play ? (play.support.includes(squad!.id) ? 'support' : 'assault') : undefined };
      const before = this.facts.get(unit.id), now = new Set<string>();
      let best: CalloutRule | null = null;
      for (const r of this.rules) {
        if (r.by === 'leader' && !c.leader) continue;
        const key = `${unit.id}:${r.id}`;
        if (r.when(c)) { if (r.hold) this.lastTrue.set(key, tick); }
        else { const t = r.hold ? this.lastTrue.get(key) : undefined; if (t === undefined || (tick - t) * PHYS_DT >= r.hold!) continue; }
        now.add(r.id);
        if (!before || before.has(r.id) || (best && r.rank <= best.rank)) continue;
        const sq = r.squad && squad ? this.squadCall.get(`${squad.id}:${r.id}`) : undefined;
        if (sq !== undefined && (tick - sq) * PHYS_DT < r.squad!) continue;
        best = r;
      }
      this.facts.set(unit.id, now);
      if (!best) continue;
      const last = this.lastCall.get(unit.id);
      if (last !== undefined && (tick - last) * PHYS_DT < CALLOUT_LOOK.rest) continue;
      this.lastCall.set(unit.id, tick);
      if (best.squad && squad) this.squadCall.set(`${squad.id}:${best.id}`, tick);
      made.push({ unitId: unit.id, squadId: squad?.id ?? null, team: unit.teamIndex, id: best.id, say: typeof best.say === 'string' ? best.say : best.say(c), tone: best.tone, at: best.at, tick });
    }
    this.live.push(...made);
    const keep = this.live.filter(k => (tick - k.tick) * PHYS_DT < CALLOUT_LOOK.show + CALLOUT_LOOK.fade);
    this.live.length = 0; this.live.push(...keep);
    return made;
  }
}

/** A call's opacity `age` s after it was made: full for `show`, fading over `fade`. Pure. */
export function calloutAlpha(age: number): number {
  const L = CALLOUT_LOOK;
  return age < 0 ? 0 : age < L.show ? 1 : Math.max(0, 1 - (age - L.show) / L.fade);
}

/**
 * Tags that would overlap, stacked: each (in the order given — the nearest, the highest rank first) keeps its place or
 * rises above the tags already placed it would cover. Boxes in px, `y` the tag's top; returns each tag's rise (px). Pure.
 */
export function stackCallouts(boxes: ReadonlyArray<{ x: number; y: number; w: number; h: number }>, gap = 2): number[] {
  const placed: Array<{ x: number; y: number; w: number; h: number }> = [];
  return boxes.map(b => {
    let y = b.y;
    for (let moved = true; moved;) {
      moved = false;
      for (const p of placed) if (b.x < p.x + p.w && p.x < b.x + b.w && y < p.y + p.h + gap && p.y < y + b.h + gap) { y = p.y - b.h - gap; moved = true; }
    }
    placed.push({ ...b, y });
    return b.y - y;
  });
}

/** A tag's box (px) for `say` at `scale` whose tail points at (x, y): its left, top, width and height. */
export function calloutBox(g: CanvasRenderingContext2D, say: string, tone: CalloutRule['tone'], x: number, y: number, scale = 1): { x: number; y: number; w: number; h: number } {
  const T = CALLOUT_TONE[tone], fs = 12 * scale, h = fs + 7 * scale;
  g.save(); g.font = `${T.weight} ${fs}px system-ui, sans-serif`;
  const w = g.measureText(say).width + 12 * scale;
  g.restore();
  return { x: x - w / 2, y: y - 5 * scale - h, w, h };
}

/** The look per tone: the drill in the team's colour, danger amber on dark, the soldier's own state quiet. */
export const CALLOUT_TONE: Record<CalloutRule['tone'], { fill: string | null; text: string; edge: string; weight: number }> = {
  drill: { fill: null, text: '#ffffff', edge: 'rgba(0,0,0,0.55)', weight: 700 },   // (null: the team's colour)
  danger: { fill: 'rgba(20,16,8,0.82)', text: '#ffd24a', edge: '#ffd24a', weight: 700 },
  self: { fill: 'rgba(12,12,24,0.7)', text: '#dfe3ea', edge: 'rgba(255,255,255,0.18)', weight: 500 },
};

/**
 * A call drawn as a speech tag whose tail points down at (x, y) — over a soldier's head at the shoulder, over a squad's
 * block in the command view; `team` its colour, `alpha` its fade (calloutAlpha), `scale` the size (1: the shoulder's 12 px),
 * `rise` px it is lifted to clear another (stackCallouts; its tail reaches down still).
 * Browser only.
 */
export function drawCallout(g: CanvasRenderingContext2D, say: string, tone: CalloutRule['tone'], x: number, y: number, team: string, alpha: number, scale = 1, rise = 0): void {
  if (alpha <= 0.01) return;
  const T = CALLOUT_TONE[tone], fs = 12 * scale, padX = 6 * scale, h = fs + 7 * scale, tail = 5 * scale;
  g.save();
  g.globalAlpha = alpha;
  g.font = `${T.weight} ${fs}px system-ui, sans-serif`;
  const w = g.measureText(say).width + padX * 2, left = x - w / 2, top = y - tail - h - rise;
  g.beginPath();
  g.roundRect(left, top, w, h, h / 2);
  g.fillStyle = T.fill ?? team; g.fill();
  g.lineWidth = Math.max(1, scale); g.strokeStyle = T.edge; g.stroke();
  g.beginPath(); g.moveTo(x - tail * (rise ? 0.5 : 1), top + h - 0.5); g.lineTo(x, y); g.lineTo(x + tail * (rise ? 0.5 : 1), top + h - 0.5); g.closePath();
  g.fillStyle = T.fill ?? team; g.fill();
  g.fillStyle = T.text; g.textAlign = 'center'; g.textBaseline = 'middle';
  g.fillText(say, x, top + h / 2 + 0.5 * scale);
  g.restore();
}
